import moment from "moment";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import {
  useToast,
  ToastType,
} from "../../../../../context_providers/toast/toast_ctx";
import {
  fetchGet,
  fetchPut,
  fetchPost,
  getHeaders,
} from "../../../../../service/api_client";
import { UTCToLocalDate } from "../../../../../utils/date_util";
import WarehouseSearchField from "../../../warehouse/warehouse_search_field";
import axios from "axios";
import { signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../../../../../service/firebase/config";
import EmployeeSearchWidget from "../../../../../ui/new_components/emp_search_widget";
import { urls } from "../../../../../utils/urls";
import {
  StockItemMovementModel,
  stockJournalDefaultValue,
  StockJournalModel,
  VoucherStatus,
} from "../../../models/stock_transfer_model";
import ItemRowStockTransfer from "./ItemRowStockTransfer";
import { WarehouseModel } from "../../../models/warehouse_model";
import ModalDialog from "../../../../../ui/new_components/common/modal";
import Papa from "papaparse";

interface StockJournalProps {}

const StockJournalCreate: FunctionComponent<StockJournalProps> = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/sender_transfer`;
  const [, setLoading] = useState(false);
  const [editable, setEditable] = useState(false);
  const [addNewId1, setAddNewId1] = useState(Math.random() * 1000);
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);
  const [isButtonClicked, setIsButtonClicked] = useState(true);
  const { showToast } = useToast();
  const [data, setData] = useState<StockJournalModel>({
    ...stockJournalDefaultValue,
  });
  const existingItems = data.source || [];
  const [availableItems, setAvailableItems] = useState([]);
  const navigate = useHistory();

  useEffect(() => {
    if (id) {
      getVoucher(id);
      setEditable(true);
    }
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleWarehouseSelect = async (selectedWarehouse: WarehouseModel) => {
    setData((prevData) => ({
      ...prevData,
      from_warehouse: selectedWarehouse,
    }));
    //call function to get list of items in warehouse.
    await getItemsInSelectedWarehouse(selectedWarehouse);
  };

  useEffect(() => {
    if (data.from_warehouse) {
      getItemsInSelectedWarehouse(data.from_warehouse);
    }
  }, [data.from_warehouse]);

  const getItemsInSelectedWarehouse = async (warehouse: WarehouseModel) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses?warehouse_id=${warehouse.id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        const { item_data } = res.data;

        setAvailableItems(item_data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
          user: prevData.user,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  async function getUserData() {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/auth/data`;
      const res = (
        await axios.get(url, {
          withCredentials: true,
          headers: { ...getHeaders() },
        })
      ).data;

      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          user: res.data || prevData.user, // Ensure existing data is preserved
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      const { response } = error;
      if (response && response.status === 401) {
        firebaseSignOut(auth);
        window.localStorage.removeItem("token");
      }
    }
    setLoading(false);
  }

  const submit = async () => {
    if (
      !data.source?.length ||
      !data.from_warehouse?.name ||
      !data.receiver_warehouse?.name ||
      !data.shipment_date
    )
      return showToast({
        type: ToastType.warning,
        text: "Please add Items and all required fields!!!!",
      });

    if (data.from_warehouse?.id === data.receiver_warehouse?.id)
      return showToast({
        type: ToastType.warning,
        text: "Sender and Receiver Warehouse cannot be the same!",
      });
    setIsButtonClicked(false);
    setLoading(true);
    const res = id
      ? await fetchPut(url + "/" + id, data)
      : await fetchPost(url, data);
    if (res.success) {
      data.status = VoucherStatus.intransit;
      setIsButtonClicked(false);
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.stock_transfer);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Device ID"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sender_device_stock_transfer.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv"].includes(ext ?? "") || !file.type.includes("csv")) {
      showToast({ type: ToastType.error, text: "Only CSV files allowed." });
      event.target.value = "";
      return;
    }
    if (!data.from_warehouse?.name) {
      showToast({ type: ToastType.error, text: "Select a warehouse first." });
      event.target.value = "";
      return;
    }

    let rowIndex = 1;
    const invalidRows: number[] = [];

    setLoading(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      worker: true,
      step: (result) => {
        rowIndex++;
        const row = result.data as Record<string, any>;
        const item = row["Item Code"]?.toString().trim();
        const device = row["Device ID"]?.toString().trim();

        if (!item || !device) {
          invalidRows.push(rowIndex);
        }
      },
      complete: async () => {
        if (invalidRows.length) {
          showToast({
            type: ToastType.error,
            text: `Mandatory fields missing at lines: ${invalidRows.join(
              ", ",
            )}`,
          });
          setLoading(false);
          event.target.value = "";
          return;
        }

        const formData = new FormData();
        formData.append("warehouse", data.from_warehouse.name);
        if (data.from_warehouse.id != null) {
          formData.append("from_warehouse_id", String(data.from_warehouse.id));
        }
        formData.append("file", file, file.name);

        try {
          const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/journal/sender_transfer/import-items`;
          const resp = await fetch(url, { method: "POST", body: formData });

          const result = await resp.json();

          if (resp.ok && result.success) {
            setData((prev) => ({
              ...prev,
              source: [...prev.source, ...result.data],
            }));
            showToast({
              type: ToastType.success,
              text: result.message || "Upload successful!",
            });
          } else {
            showToast({
              type: ToastType.error,
              text: result.error || result.message || "Upload failed.",
            });
          }
        } catch (err: any) {
          showToast({
            type: ToastType.error,
            text: "Upload error: " + (err.message || err),
          });
        } finally {
          setLoading(false);
          event.target.value = "";
        }
      },
      error: (err) => {
        showToast({
          type: ToastType.error,
          text: "CSV parse error: " + err.message,
        });
        setLoading(false);
        event.target.value = "";
      },
    });
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Items</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}
      <div className="sticky top-0 mt-2 px-6 h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Stock Transfer
          </div>
        </div>
        <div className="bg-white rounded mb-5 flex gap-1 h-auto overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 p-5 text-sm w-3/4 font-semibold rounded-t top-0">
            <div className="">
              <div className="font-bold text-md mb-3">From</div>
              <div className="">
                <div className="">
                  <div className="font-semibold w-full mt-1">Sender Name</div>
                  <input
                    value={`${data.user?.first_name || ""} ${
                      data.user?.last_name || ""
                    }`}
                    placeholder="Sender"
                    type="text"
                    disabled={!editable}
                    className="text-left p-1 focus:outline-none w-full"
                    onChange={(e) => {
                      setData((prevData) => ({
                        ...prevData,
                        user: {
                          ...prevData.user!,
                          first_name: e.target.value,
                          last_name: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="font-semibold w-full mt-1">
                  Sender Department
                </div>
                <input
                  value={data.user?.emp_profile?.department}
                  placeholder="Department"
                  disabled={!editable}
                  type="text"
                  className="text-left p-1 focus:outline-none w-full"
                  onChange={(e) =>
                    setData((d) => ({
                      ...d,
                      department: d.user?.emp_profile?.department,
                    }))
                  }
                />
              </div>
              <div className="font-semibold w-full mt-1">From Warehouse *</div>
              <WarehouseSearchField
                type={""}
                disabled={editable}
                value={data.from_warehouse}
                placeholder="Inventory Basket"
                onSelect={handleWarehouseSelect}
                // onSelect={(d) => {
                //   setData((old) => {
                //     return {
                //       ...old,
                //       from_warehouse: d,
                //     };
                //   });
                // }}
              />
              <div className="">
                <label htmlFor="" className="font-semibold w-full mt-1">
                  Shipment Date *
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    data.shipment_date
                      ? moment(data.shipment_date).format("YYYY-MM-DD")
                      : ""
                  }
                  // min={moment().format("YYYY-MM-DD")}
                  onChange={(e) => {
                    const date = UTCToLocalDate(e.target.value)!;
                    setData((o) => ({
                      ...o,
                      shipment_date: date,
                    }));
                  }}
                />
              </div>
              <div className="mt-1">
                <label htmlFor="" className="font-semibold w-full mt-1">
                  Purchase Order ID
                </label>
                <input
                  placeholder="Purchase Order ID"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="purchase_order_id"
                  name="purchase_order_id"
                  value={data.purchase_order_id}
                  onChange={(e) =>
                    setData((prevData) => ({
                      ...prevData,
                      purchase_order_id: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="mr-0">
              <div className="font-bold text-md mb-3">To</div>

              <div className="font-semibold w-full mt-1">
                Warehouse/office *
              </div>
              <WarehouseSearchField
                type={""}
                disabled={editable}
                value={data.receiver_warehouse}
                placeholder="Inventory Basket"
                onSelect={(d) => {
                  setData((old) => {
                    return {
                      ...old,
                      receiver_warehouse: d,
                      //   department:d.spoc?.department,
                      //   contact_name:d.spoc?.contact_name
                    };
                  });
                }}
              />
              {/* <div className="font-bold">Receiver</div>
              <input
                value={data.receiver_warehouse?.spoc?.contact_name}
                placeholder=""
                type="text"
                className="text-left bg-gray-200 p-1 focus:outline-none border rounded w-full"
                onChange={(e) => {
                  setData((prevData) => ({
                    ...prevData,
                    receiver_warehouse: {
                      ...prevData.receiver_warehouse!,
                      contact_name: e.target.value,
                    },
                  }));
                }}
                readOnly
              />

              <div className="flex flex-col items-start text-sm w-full">
                <label className="font-semibold w-full" htmlFor="line1">
                  Receiver Department
                </label>
                <input
                  placeholder="dept"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="dept"
                  name="dept"
                  value={data.receiver_warehouse?.spoc?.department}
                  onChange={(e) =>
                    setData((prevData) => ({
                      ...prevData,
                      receiver_warehouse: {
                        ...prevData.receiver_warehouse!,
                        department: e.target.value,
                      },
                    }))
                  }
                />
              </div> */}
              <div className="">
                <div className="font-semibold w-full mt-1">
                  Delivery Challan Number/Invoice
                </div>
                <input
                  value={data.delivery_challan_number}
                  name="challan"
                  id="challan"
                  placeholder="Delivery Challan"
                  type="text"
                  className="text-left p-1 focus:outline-none border rounded w-full"
                  onChange={(e) =>
                    setData((d) => ({
                      ...d,
                      delivery_challan_number: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-col items-start text-sm w-full">
                <label className="font-semibold w-full mt-1" htmlFor="line1">
                  Tracking ID
                </label>
                <input
                  placeholder="track id"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="track_id"
                  name="track_id"
                  value={data.tracking_id}
                  onChange={(e) =>
                    setData((prevData) => ({
                      ...prevData,
                      tracking_id: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-col gap-1 w-full">
                <label htmlFor="" className="font-semibold w-full mt-1">
                  Expected Received Date
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    data?.expected_received_date
                      ? moment(data?.expected_received_date).format(
                          "YYYY-MM-DD",
                        ) // Format to YYYY-MM-DD for input
                      : "" // Empty by default, so no date is selected initially
                  }
                  min={moment().format("YYYY-MM-DD")} // Prevent selection of past dates
                  onChange={(e) => {
                    const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                    const date = selectedDate
                      ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Set the time to 00:00:00
                      : undefined; // If no date is selected, reset the value

                    // Update the state with the selected date
                    setData((pv) => ({
                      ...pv,
                      expected_received_date: date!, // Save the selected date in state
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 mt-5 ">
          <div className="flex flex-col gap-5">
            <div className="">
              <div className="flex gap-2 items-center justify-between">
                <h3 className="text-sm font-bold">Items</h3>
                <label
                  onClick={handleDownloadTemplate}
                  className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                >
                  Template
                </label>
                <label className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1 inline-block">
                  Upload Product Items
                  <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleCSVUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="bg-white rounded flex flex-col gap-1 h-auto overflow-auto pb-20">
                <div className="grid grid-cols-7 gap-1 p-1 bg-myPrimaryColor text-white text-sm font-semibold rounded-t sticky top-0">
                  <div className="text-left">Item Code</div>
                  <div className="text-left col-span-2">Item Name</div>
                  <div className="text-left ">Unit Measure</div>
                  {/* <div className="text-left">From Warehouse</div> */}
                  <div className="text-left">Qty</div>
                  <div className="text-left">Device IDs</div>
                  <div className="text-center">Action</div>
                </div>
                <div>
                  {data.source?.map((comp, i) => {
                    return (
                      <ItemRowStockTransfer
                        component={comp}
                        from_warehouse={data.from_warehouse}
                        edit={false}
                        disabled={false}
                        key={comp.id}
                        onDelete={(d: any) => {
                          setData((o) => {
                            const source = [
                              ...(o.source || []).filter(
                                (it) => it.id !== d.id,
                              ),
                            ];

                            return {
                              ...o,
                              source,
                            };
                          });
                        }}
                        onSubmit={(d: any) => {
                          setData((o) => {
                            const source = [
                              ...(o.source || []).map((it) =>
                                it.id === d.id ? d : it,
                              ),
                            ];

                            return {
                              ...o,
                              source,
                            };
                          });
                        }}
                        existingItems={existingItems}
                        availableItems={availableItems}
                      />
                    );
                  })}

                  {!data.id && (
                    <ItemRowStockTransfer
                      key={addNewId1}
                      from_warehouse={data?.from_warehouse}
                      onSubmit={function (data: StockItemMovementModel): void {
                        data.id = (Math.random() * 1000).toString();
                        setData((o) => {
                          const source = [...(o.source || []), data];

                          return {
                            ...o,
                            source,
                          };
                        });
                        setAddNewId1(Math.random() * 1000);
                      }}
                      // index={i}
                      edit={true}
                      disabled={false}
                      existingItems={existingItems}
                      availableItems={availableItems}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex sticky bottom-0 justify-end my-5 gap-4 px-5 py-5">
        {/* {id && !data.destination?.length ? (
            <button
              type="button"
              className="bg-red-500 px-10 py-1 rounded-md text-white text-sm"
              onClick={withdraw}
              disabled={isWithdrawn} // Disable if already withdrawn
            >
              Withdraw
            </button>
          ) : null}
          {id &&
            !(data.status === voucherStatus.return) &&
            !data.destination?.length && (
              <button
                type="button"
                className="bg-myPrimaryColor px-10 py-1 rounded-md text-white text-sm"
                onClick={submit}
                disabled={isWithdrawn} // Disable if already withdrawn
              >
                Resend Mail
              </button>
            )} */}

        {/* {!id && isButtonClicked && ( */}

        {!id && (
          <button
            type="button"
            className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
            onClick={submit}
            // disabled={isWithdrawn} // Disable submit if withdrawn or no items
          >
            Send
          </button>
        )}
        {id &&
          !data.destination?.length &&
          data.status === VoucherStatus.intransit && (
            <button
              type="button"
              className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
              onClick={submit}
              // disabled={isWithdrawn} // Disable submit if withdrawn or no items
            >
              Update
            </button>
          )}
        <button
          type="button"
          className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
          onClick={() => navigate.push(urls.stock_transfer)}
        >
          Quit
        </button>
      </div>
      {/* )} */}
    </>
  );
};

export default StockJournalCreate;
